/**
 * PAX — usePwaInstall hook
 *
 * Zapewnia state machine dla całego cyklu instalacji PWA:
 *
 *  STATES:
 *  - installed:        aplikacja już uruchomiona jako PWA (standalone / iOS home screen)
 *  - installable:      beforeinstallprompt dostępny → można natychmiast zainstalować
 *  - ios-manual:       iOS Safari — brak beforeinstallprompt, wymagana instrukcja ręczna
 *  - dismissed:        użytkownik odrzucił prompt niedawno → nie pokazujemy przez COOLDOWN_DAYS
 *  - unsupported:      przeglądarka nie wspiera instalacji
 *
 *  API:
 *  - state              — aktualny stan (jeden z powyższych)
 *  - promptInstall()    — otwiera natywny prompt (jeśli installable)
 *  - dismiss()          — użytkownik zamknął nasz baner (nie natywny) → cooldown
 *  - canPromptAgain     — czy minął cooldown od ostatniego dismiss
 *
 *  Kluczowe wykrywanie "aplikacja już zainstalowana":
 *   - display-mode: standalone   (Android/Chrome/Edge)
 *   - display-mode: minimal-ui   (some browsers)
 *   - navigator.standalone       (iOS Safari)
 *   - referrer android-app://    (Chrome TWA/PWA)
 *
 *  Nie pokazujemy propozycji instalacji gdy:
 *   - aplikacja już zainstalowana
 *   - jesteśmy na /login (OAuth in progress)
 *   - jesteśmy na callbacku OAuth/bankowym (np. /psd2/callback — przyszła integracja)
 *   - beforeinstallprompt niedostępny AND nie jesteśmy na iOS
 *
 *  Cooldown mechanism:
 *   - localStorage: 'pax:pwa:dismissedAt' = timestamp ms
 *   - COOLDOWN_DAYS = 7 (rozsądny balance między nachalnością a dostępnością)
 *   - "Zainstaluj aplikację" w Ustawieniach ZAWSZE dostępne (ignoruje cooldown)
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const DISMISSED_KEY = 'pax:pwa:dismissedAt';
const COOLDOWN_DAYS = 7;
const COOLDOWN_MS = COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

const AUTH_PATHS = ['/login', '/oauth', '/psd2/callback', '/bank/callback'];

function isStandaloneMode() {
  if (typeof window === 'undefined') return false;
  // Standard PWA display-mode
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  if (window.matchMedia && window.matchMedia('(display-mode: minimal-ui)').matches) return true;
  if (window.matchMedia && window.matchMedia('(display-mode: fullscreen)').matches) return true;
  // iOS Safari — Web App added to Home Screen
  if (typeof window.navigator.standalone === 'boolean' && window.navigator.standalone) return true;
  // Trusted Web Activity / Chrome PWA (Android)
  if (document.referrer && document.referrer.startsWith('android-app://')) return true;
  return false;
}

function detectIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPad iOS 13+ deklaruje się jako Mac — sprawdzamy touch
  const iPadOS13Up = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || iPadOS13Up;
}

function detectIOSSafari() {
  if (!detectIOS()) return false;
  const ua = navigator.userAgent || '';
  // Wykluczamy Chrome iOS (CriOS), Firefox iOS (FxiOS), Edge iOS (EdgiOS) — one nie mają Add to Home Screen w Share
  return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

function isOnAuthPath() {
  if (typeof window === 'undefined') return false;
  return AUTH_PATHS.some((p) => window.location.pathname.startsWith(p));
}

function loadDismissedAt() {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return 0;
    const n = parseInt(raw, 10);
    return isNaN(n) ? 0 : n;
  } catch {
    return 0;
  }
}

export function usePwaInstall() {
  const [installed, setInstalled] = useState(() => isStandaloneMode());
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissedAt, setDismissedAt] = useState(() => loadDismissedAt());
  const isIOS = useRef(detectIOSSafari());

  // Nasłuchiwanie beforeinstallprompt (Android Chrome, Samsung, Edge)
  useEffect(() => {
    const onBeforeInstall = (e) => {
      // Blokujemy natychmiastowy prompt Chrome, cachujemy event by pokazać go
      // dopiero po interakcji użytkownika (bardziej user-friendly)
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      // Reset cooldown po instalacji (nie potrzeba już)
      try {
        localStorage.removeItem(DISMISSED_KEY);
      } catch {
        /* noop */
      }
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Nasłuchiwanie zmiany trybu wyświetlania (użytkownik zainstalował z innego karty)
  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia('(display-mode: standalone)');
    const onChange = () => setInstalled(isStandaloneMode());
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return { outcome: 'unsupported' };
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (choice.outcome === 'dismissed') {
        const now = Date.now();
        try {
          localStorage.setItem(DISMISSED_KEY, String(now));
        } catch {
          /* noop */
        }
        setDismissedAt(now);
      }
      return choice; // { outcome: 'accepted' | 'dismissed' }
    } catch (err) {
      return { outcome: 'error', error: err };
    }
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    const now = Date.now();
    try {
      localStorage.setItem(DISMISSED_KEY, String(now));
    } catch {
      /* noop */
    }
    setDismissedAt(now);
  }, []);

  const resetDismiss = useCallback(() => {
    try {
      localStorage.removeItem(DISMISSED_KEY);
    } catch {
      /* noop */
    }
    setDismissedAt(0);
  }, []);

  const canPromptAgain = dismissedAt === 0 || Date.now() - dismissedAt > COOLDOWN_MS;
  const onAuthPath = isOnAuthPath();

  // State resolution — priorytet: installed > path lockout > installable > iOS manual > unsupported
  let state = 'unsupported';
  if (installed) state = 'installed';
  else if (onAuthPath) state = 'suppressed'; // nie pokazuj na ekranach OAuth
  else if (deferredPrompt) state = 'installable';
  else if (isIOS.current) state = 'ios-manual';
  else if (!canPromptAgain) state = 'dismissed';

  return {
    state,
    installed,
    installable: !!deferredPrompt,
    isIOS: isIOS.current,
    canPromptAgain,
    promptInstall,
    dismiss,
    resetDismiss,
  };
}
